import { useEffect, useState } from 'react';
import { analyticsApi, buildAnalyticsQuery, type AnalyticsFilters, type AnalyticsRetention } from '../../services/analytics';
import { DASH } from './format';
import { retentionCell, retentionHeaders } from './retention';
import styles from './RetentionHeatmap.module.css';

interface RetentionHeatmapProps {
    filters: AnalyticsFilters;
    /** Muda quando a pessoa pede "tentar novamente" no painel. */
    refreshKey: number;
}

interface Settled {
    key: string;
    error: string | null;
}

export function RetentionHeatmap({ filters, refreshKey }: RetentionHeatmapProps) {
    const [data, setData] = useState<AnalyticsRetention | null>(null);
    const [settled, setSettled] = useState<Settled | null>(null);
    const [retry, setRetry] = useState(0);

    const requestKey = `${buildAnalyticsQuery(filters)}#${refreshKey}.${retry}`;

    useEffect(() => {
        const controller = new AbortController();
        analyticsApi
            .retention(filters, controller.signal)
            .then((d) => {
                setData(d);
                setSettled({ key: requestKey, error: null });
            })
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setSettled({
                    key: requestKey,
                    error: e instanceof Error ? e.message : 'Não foi possível carregar a retenção.',
                });
            });
        return () => controller.abort();
        // requestKey resume filtros e contadores de nova tentativa.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestKey]);

    const loading = settled?.key !== requestKey;
    const error = settled && settled.key === requestKey ? settled.error : null;
    const headers = data ? retentionHeaders(data.months) : [];

    return (
        <section className={`${styles.card} ${loading ? styles.loading : ''}`} aria-busy={loading}>
            <header className={styles.header}>
                <h4 className={styles.title}>Retenção geral por safra de contas</h4>
                <p className={styles.subtitle}>
                    Contas criadas no mês que tiveram ao menos um acesso em cada mês. Uma conta conta uma vez, mesmo com
                    vários usuários.
                </p>
            </header>

            {error && (
                <div className={styles.error} role="alert">
                    <span>{error}</span>
                    <button type="button" className={styles.retry} onClick={() => setRetry((n) => n + 1)}>
                        Tentar novamente
                    </button>
                </div>
            )}

            {!error && !data && <p className={styles.message}>Carregando...</p>}

            {data && (
                <div className={styles.scroll}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th scope="col" className={styles.left}>
                                    Safra
                                </th>
                                <th scope="col">Contas</th>
                                {headers.map((h) => (
                                    <th key={h} scope="col">
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {data.rows.map((row) => (
                                <tr key={row.month}>
                                    <th scope="row" className={`${styles.safra} ${styles.left}`}>
                                        {row.month.slice(0, 7)}
                                    </th>
                                    <td className={styles.size}>{row.size}</td>
                                    {data.months.map((m, i) => {
                                        const cell = retentionCell(row.cols.find((c) => c.offset === i));
                                        return (
                                            <td key={m}>
                                                {cell ? (
                                                    <div
                                                        className={styles.heat}
                                                        style={{
                                                            backgroundColor: `color-mix(in srgb, var(--color-primary-600) ${Math.round(30 + cell.intensity * 25)}%, var(--color-white))`,
                                                        }}
                                                    >
                                                        <strong>{cell.pct}</strong>
                                                        <span>{cell.count}</span>
                                                    </div>
                                                ) : (
                                                    <span className={styles.dash}>{DASH}</span>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {data.rows.length === 0 && <p className={styles.message}>Sem safras no período.</p>}
                </div>
            )}
        </section>
    );
}
